import { useState, useEffect } from 'react';
import { UserProgress } from '../services/storage';
import { dialogs, Dialog } from '../data/dialogs';
import { ttsService } from '../services/tts';
import { storageService } from '../services/storage';
import SentenceListeningPage from './SentenceListeningPage';

interface ListeningPageProps {
  user: UserProgress;
}

type ListeningMode = 'menu' | 'dialog' | 'sentence';

export default function ListeningPage({ user }: ListeningPageProps) {
  const [listeningMode, setListeningMode] = useState<ListeningMode>('menu');
  const [selectedDialog, setSelectedDialog] = useState<Dialog | null>(null);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [completedDialogs, setCompletedDialogs] = useState<string[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [selectedScene, setSelectedScene] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [autoPlay, setAutoPlay] = useState(false); // Auto-play mode
  const [dialogCompleted, setDialogCompleted] = useState(false); // Dialog completed flag

  // Auto-play effect - must be at top level before any conditional returns
  useEffect(() => {
    if (autoPlay && !isPlaying && !dialogCompleted && selectedDialog) {
      handlePlayLine();
    }
  }, [currentLineIndex, autoPlay]);

  // Get scene icon

  // Get scene icon
  const getSceneIcon = (scene: string): string => {
    const icons: { [key: string]: string } = {
      '学校': '🏫',
      '教室': '🏫',
      '家里': '🏠',
      '客厅': '🏠',
      '卧室': '🛏️',
      '公园': '🌳',
      '街道': '🛣️',
      '商店': '🏪',
      '书店': '📚',
      '图书馆': '📚',
      '医院': '🏥',
      '动物园': '🦁',
      '博物馆': '🏛️',
      '运动场': '⚽',
      '游泳池': '🏊',
      '厨房': '🍳',
      '餐厅': '🍽️',
      '书房': '📖',
      '学校食堂': '🍽️',
      '操场': '🏃',
      '地铁': '🚇',
      '机场': '✈️',
      '超市': '🛒',
      '服装店': '👔',
      '玩具店': '🧸',
      '文具店': '✏️',
      '水果市场': '🍎',
      '市场': '🛒',
      '朋友家': '🏠',
      '户外': '🌳',
      '地铁站': '🚇',
    };
    return icons[scene] || '📍';
  };

  // Get unique scenes
  const scenes = Array.from(new Set(dialogs.map(d => d.scene)));

  // Filter dialogs
  const filteredDialogs = dialogs.filter(d => {
    if (selectedLevel !== 'all' && d.level !== selectedLevel) return false;
    if (selectedScene !== 'all' && d.scene !== selectedScene) return false;

    // Search filter (中英文模糊搜索)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const matchTitle = d.title.toLowerCase().includes(query);
      const matchScene = d.scene.toLowerCase().includes(query);
      const matchKeywords = d.keywords.some(k => k.toLowerCase().includes(query));
      const matchLines = d.lines.some(line =>
        line.text.toLowerCase().includes(query) ||
        (line.translation && line.translation.toLowerCase().includes(query))
      );
      if (!matchTitle && !matchScene && !matchKeywords && !matchLines) return false;
    }

    return true;
  });

  // Render main menu
  if (listeningMode === 'menu') {
    return (
      <div className="space-y-8 animate-slide-up">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">🎧 听力练习</h1>
          <p className="text-gray-500">选择练习类型开始学习</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Dialog Listening */}
          <button
            onClick={() => setListeningMode('dialog')}
            className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl p-8 text-white shadow-xl hover:scale-105 transition-transform"
          >
            <div className="text-center">
              <span className="text-6xl mb-4 block">💬</span>
              <h3 className="text-2xl font-bold mb-2">对话听力</h3>
              <p className="text-sm opacity-90">听日常对话，提高理解能力</p>
              <p className="text-sm opacity-75 mt-2">包含 {dialogs.length} 个场景对话</p>
            </div>
          </button>

          {/* Sentence Listening */}
          <button
            onClick={() => setListeningMode('sentence')}
            className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-3xl p-8 text-white shadow-xl hover:scale-105 transition-transform"
          >
            <div className="text-center">
              <span className="text-6xl mb-4 block">📝</span>
              <h3 className="text-2xl font-bold mb-2">句子听力</h3>
              <p className="text-sm opacity-90">听句子练习，强化听力技巧</p>
              <p className="text-sm opacity-75 mt-2">支持多种练习模式</p>
            </div>
          </button>
        </div>

        {/* Tips */}
        <div className="bg-yellow-50 rounded-3xl p-6 border-2 border-yellow-200">
          <h3 className="font-bold text-yellow-800 mb-2">💡 练习建议</h3>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• 对话听力适合练习日常交际场景</li>
            <li>• 句子听力适合强化关键词和语法结构</li>
            <li>• 建议先听对话，再做句子听力练习</li>
            <li>• 反复听，直到完全理解为止</li>
          </ul>
        </div>
      </div>
    );
  }

  // Render sentence listening
  if (listeningMode === 'sentence') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setListeningMode('menu')}
          className="text-gray-500 hover:text-gray-700 flex items-center gap-2"
        >
          <span>←</span>
          <span>返回主菜单</span>
        </button>
        <SentenceListeningPage user={user} />
      </div>
    );
  }

  // Dialog listening (original code continues...)
  const handleSelectDialog = (dialog: Dialog) => {
    setSelectedDialog(dialog);
    setCurrentLineIndex(0);
    setShowTranslation(false);
  };

  const handlePlayLine = async () => {
    if (!selectedDialog) return;

    const currentLine = selectedDialog.lines[currentLineIndex];
    setIsPlaying(true);

    try {
      await ttsService.speakNormal(currentLine.text);

      // If auto-play mode and not last line, automatically play next
      if (autoPlay && currentLineIndex < selectedDialog.lines.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 800));
        setCurrentLineIndex(currentLineIndex + 1);
      } else if (autoPlay && currentLineIndex === selectedDialog.lines.length - 1) {
        // Dialog completed
        setDialogCompleted(true);
        setAutoPlay(false);
        if (!completedDialogs.includes(selectedDialog.id)) {
          setCompletedDialogs([...completedDialogs, selectedDialog.id]);
          storageService.addPoints(user.id, 20);
          storageService.addLearningRecord(user.id, 'listening', selectedDialog.id, 100, 60);
        }
      }
    } catch (error) {
      console.error('TTS error:', error);
      setAutoPlay(false);
    }

    setIsPlaying(false);
  };

  const handleNextLine = () => {
    if (!selectedDialog) return;

    const nextIndex = currentLineIndex + 1;
    if (nextIndex < selectedDialog.lines.length) {
      setCurrentLineIndex(nextIndex);
      setShowTranslation(false);
    } else {
      // Dialog completed
      const dialogId = selectedDialog.id;
      if (!completedDialogs.includes(dialogId)) {
        setCompletedDialogs([...completedDialogs, dialogId]);
        storageService.addPoints(user.id, 20);
        storageService.addLearningRecord(user.id, 'listening', dialogId, 100, 60);
      }
      setDialogCompleted(true);
    }
  };

  // Toggle auto-play mode
  const toggleAutoPlay = () => {
    if (!autoPlay) {
      // Start auto-play
      setAutoPlay(true);
      setDialogCompleted(false);
    } else {
      // Stop auto-play
      setAutoPlay(false);
    }
  };

  // Restart dialog
  const handleRestartDialog = () => {
    setCurrentLineIndex(0);
    setDialogCompleted(false);
    setShowTranslation(false);
    setAutoPlay(false);
  };

  // Play next dialog
  const handlePlayNextDialog = () => {
    const currentIndex = filteredDialogs.findIndex(d => d.id === selectedDialog?.id);
    const nextDialog = filteredDialogs[currentIndex + 1] || filteredDialogs[0];

    if (nextDialog) {
      setSelectedDialog(nextDialog);
      setCurrentLineIndex(0);
      setDialogCompleted(false);
      setShowTranslation(false);
      setAutoPlay(false);
    }
  };

  const handlePlayFullDialog = async () => {
    if (!selectedDialog) return;

    setIsPlaying(true);

    for (const line of selectedDialog.lines) {
      try {
        await ttsService.speakNormal(line.text);
        // Small pause between lines
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('TTS error:', error);
        break;
      }
    }

    setIsPlaying(false);
  };

  // Dialog selection view
  if (!selectedDialog) {
    return (
      <div className="space-y-6 animate-slide-up">
        {/* Compact Header */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl p-6 text-white shadow-xl">
          <button
            onClick={() => setListeningMode('menu')}
            className="mb-3 text-white/80 hover:text-white flex items-center gap-2 text-sm"
          >
            <span>←</span>
            <span>返回主菜单</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1">💬 对话听力</h1>
              <p className="text-sm text-white/80">
                {dialogs.length} 个对话 {filteredDialogs.length !== dialogs.length && `· 筛选: ${filteredDialogs.length} 个`}
              </p>
            </div>
            <div className="flex gap-2">
              {/* Level Filter Pills */}
              <button
                onClick={() => setSelectedLevel(selectedLevel === 'all' ? 'beginner' : selectedLevel === 'beginner' ? 'intermediate' : 'all')}
                className="px-3 py-1 bg-white/20 rounded-full text-sm hover:bg-white/30"
              >
                {selectedLevel === 'all' ? '全部难度' : selectedLevel === 'beginner' ? '初级' : '中级'}
              </button>
            </div>
          </div>

          {/* Compact Search */}
          <div className="mt-3 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="🔍 搜索对话..."
              className="w-full px-4 py-2 pr-10 rounded-xl bg-white/95 text-gray-800 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Compact Scene Selection */}
        <div className="bg-white rounded-2xl p-4 shadow-soft">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => setSelectedScene('all')}
              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                selectedScene === 'all'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              📚 全部
            </button>
            {scenes.slice(0, 10).map(scene => {
              const sceneIcon = getSceneIcon(scene);
              return (
                <button
                  key={scene}
                  onClick={() => setSelectedScene(scene)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    selectedScene === scene
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {sceneIcon} {scene}
                </button>
              );
            })}
          </div>
        </div>

        {/* Dialog Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredDialogs.map(dialog => {
            const isCompleted = completedDialogs.includes(dialog.id);
            return (
              <button
                key={dialog.id}
                onClick={() => handleSelectDialog(dialog)}
                className={`bg-white rounded-3xl p-6 shadow-soft card-hover ${
                  isCompleted ? 'border-2 border-green-400' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <h3 className="font-bold text-xl text-gray-800 mb-1">
                      {dialog.title}
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">
                      场景：{dialog.scene}
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <span className={`px-3 py-1 rounded-full ${
                        dialog.level === 'beginner'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {dialog.level === 'beginner' ? '初级' : '中级'}
                      </span>
                      <span className="text-gray-400">
                        {dialog.lines.length} 句对话
                      </span>
                    </div>
                  </div>
                  {isCompleted && (
                    <span className="text-3xl">✅</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Dialog practice view
  const currentLine = selectedDialog.lines[currentLineIndex];

  // Dialog completed view
  if (dialogCompleted) {
    const currentIndex = filteredDialogs.findIndex(d => d.id === selectedDialog.id);
    const nextDialog = filteredDialogs[currentIndex + 1];

    return (
      <div className="space-y-6 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSelectedDialog(null)}
            className="text-gray-500 hover:text-gray-700 flex items-center gap-2"
          >
            <span>←</span>
            <span>返回列表</span>
          </button>
          <h2 className="font-bold text-xl text-gray-800">{selectedDialog.title}</h2>
        </div>

        {/* Completed celebration */}
        <div className="bg-gradient-to-r from-green-400 to-blue-500 rounded-3xl p-8 text-white text-center shadow-xl animate-bounce-in">
          <div className="text-8xl mb-6 animate-celebrate">🎉</div>
          <h2 className="text-3xl font-bold mb-2">对话完成！</h2>
          <p className="text-xl opacity-90">你成功听完了整个对话</p>
          <div className="mt-4 text-lg">
            🏆 获得 <span className="font-bold text-2xl">20</span> 积分
          </div>
        </div>

        {/* Keywords summary */}
        <div className="bg-white rounded-3xl p-6 shadow-soft">
          <h3 className="font-bold text-gray-700 mb-4 text-lg">📚 关键词汇总结</h3>
          <div className="flex flex-wrap gap-2">
            {selectedDialog.keywords.map((keyword, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full font-medium"
              >
                {keyword}
              </span>
            ))}
          </div>
        </div>

        {/* Action buttons */}
        <div className="space-y-3">
          {/* Replay button */}
          <button
            onClick={handleRestartDialog}
            className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl font-bold text-lg hover:opacity-90 transition-opacity"
          >
            🔄 重听对话
          </button>

          {/* Next dialog recommendation */}
          {nextDialog && (
            <button
              onClick={handlePlayNextDialog}
              className="w-full py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-2xl font-bold text-lg hover:opacity-90 transition-opacity"
            >
              ⏭️ 下一个对话：{nextDialog.title}
            </button>
          )}

          {/* Return to list */}
          <button
            onClick={() => setSelectedDialog(null)}
            className="w-full py-4 bg-gray-100 text-gray-700 rounded-2xl font-bold text-lg hover:bg-gray-200 transition-colors"
          >
            📋 返回对话列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setSelectedDialog(null)}
          className="text-gray-500 hover:text-gray-700 flex items-center gap-2"
        >
          <span>←</span>
          <span>返回列表</span>
        </button>
        <h2 className="font-bold text-xl text-gray-800">{selectedDialog.title}</h2>
      </div>

      {/* Progress */}
      <div className="bg-white rounded-2xl p-4 shadow-soft">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">
            第 {currentLineIndex + 1} / {selectedDialog.lines.length} 句
          </span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-bar-fill"
            style={{ width: `${((currentLineIndex + 1) / selectedDialog.lines.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Current line card */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl p-8 text-white shadow-xl">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-xl font-bold">
            {currentLine.speaker}
          </div>
          <div className="flex-1">
            <p className="text-sm text-white/60 mb-1">{currentLine.speaker} 说：</p>
            <p className="text-2xl font-bold">{currentLine.text}</p>
          </div>
        </div>

        {showTranslation && currentLine.translation && (
          <div className="bg-white/10 rounded-2xl p-4 mb-6 animate-slide-up">
            <p className="text-lg">{currentLine.translation}</p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            onClick={handlePlayLine}
            disabled={isPlaying}
            className={`flex-1 py-3 rounded-2xl font-bold transition-all ${
              isPlaying ? 'bg-white/10 opacity-50' : 'bg-white/20 hover:bg-white/30'
            }`}
          >
            🔊 {isPlaying ? '播放中...' : '播放这句'}
          </button>
          <button
            onClick={() => setShowTranslation(!showTranslation)}
            className="px-6 py-3 bg-white/20 rounded-2xl font-bold hover:bg-white/30 transition-all"
          >
            {showTranslation ? '🙈 隐藏翻译' : '👀 显示翻译'}
          </button>
        </div>
      </div>

      {/* Control buttons */}
      <div className="space-y-3">
        {/* Auto-play toggle */}
        <div className="bg-white rounded-2xl p-4 shadow-soft">
          <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{autoPlay ? '▶️' : '⏸️'}</span>
              <div>
                <p className="font-bold text-gray-800">自动播放模式</p>
                <p className="text-sm text-gray-500">
                  {autoPlay ? '已开启 - 自动播放下一句' : '已关闭 - 手动控制播放'}
                </p>
              </div>
            </div>
            <div
              onClick={toggleAutoPlay}
              className={`w-14 h-8 rounded-full transition-all ${
                autoPlay ? 'bg-green-500' : 'bg-gray-300'
              } relative cursor-pointer`}
            >
              <div
                className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-all ${
                  autoPlay ? 'right-1' : 'left-1'
                }`}
              />
            </div>
          </label>
        </div>

        {/* Action buttons */}
        <div className="flex gap-4">
          <button
            onClick={handlePlayFullDialog}
            disabled={isPlaying}
            className="flex-1 py-4 bg-blue-100 text-blue-700 rounded-2xl font-bold hover:bg-blue-200 transition-colors"
          >
            🎧 播放完整对话
          </button>
          <button
            onClick={handleNextLine}
            disabled={autoPlay}
            className={`flex-1 py-4 rounded-2xl font-bold transition-opacity ${
              autoPlay
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:opacity-90'
            }`}
          >
            下一句 →
          </button>
        </div>
      </div>

      {/* All lines preview */}
      <div className="bg-white rounded-3xl p-6 shadow-soft">
        <h3 className="font-bold text-gray-700 mb-4">完整对话预览</h3>
        <div className="space-y-2">
          {selectedDialog.lines.map((line, index) => (
            <div
              key={index}
              className={`p-3 rounded-xl ${
                index === currentLineIndex
                  ? 'bg-indigo-100 border-2 border-indigo-400'
                  : 'bg-gray-50'
              }`}
            >
              <span className="font-bold text-gray-600">{line.speaker}: </span>
              <span className="text-gray-800">{line.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}